<template>
  <header class="map-summary" :class="{ 'map-summary--stale': stale }">
    <!--
      A grade das faixas mora aqui dentro, e não no <header>: o cabeçalho é o
      container que ela consulta, e a consulta só alcança os descendentes.
    -->
    <div class="map-summary__layout">
      <div class="map-summary__brand">
        <img class="map-summary__logo" :src="logoSrc" alt="Sigma ECM">
        <div class="map-summary__identity">
          <h1 class="map-summary__title">{{ $t('attention-map-panel.title') }}</h1>
          <p class="map-summary__scope">{{ scopeLabel }}</p>
        </div>
      </div>

      <ul class="map-summary__counters">
        <li
          v-for="counter in counters"
          :key="counter.condition"
          class="map-summary__counter"
          :class="[
            `map-summary__counter--${counter.condition}`,
            { 'map-summary__counter--empty': !counter.value },
          ]"
        >
          <s-icon class="map-summary__counter-icon" :name="counter.icon" />
          <span class="map-summary__counter-value">{{ counter.value }}</span>
          <span class="map-summary__counter-label">
            {{ $t(`attention-map-panel.counter.${counter.condition}`) }}
          </span>
        </li>
      </ul>

      <div class="map-summary__freshness">
        <p class="map-summary__age">{{ ageLabel }}</p>
      </div>
    </div>
  </header>
</template>

<script>
import { assetConditions, assetConditionsBySeverity } from '@/domain/enums/assetCondition';
import { describeAge } from '@/domain/services/generalPanel/communicationStatusPanel/dataAge';

/**
 * Ícone de cada Condição no Placar. Lida a três metros, a forma chega antes do
 * número: o operador identifica a coluna sem precisar ler o rótulo.
 */
const CONDITION_ICONS = {
  [assetConditions.CRITICAL]: 'exclamation-circle',
  [assetConditions.ATTENTION]: 'exclamation-triangle',
  [assetConditions.NORMAL]: 'check-circle',
  [assetConditions.OUT_OF_SERVICE]: 'minus-circle',
};

const COUNTER_KEYS = {
  [assetConditions.CRITICAL]: 'critical',
  [assetConditions.ATTENTION]: 'attention',
  [assetConditions.NORMAL]: 'normal',
  [assetConditions.OUT_OF_SERVICE]: 'outOfService',
};

export default {
  name: 'MapSummary',
  props: {
    summary: {
      type: Object,
      required: true,
    },
    /** Nulo enquanto nenhum ciclo terminou. */
    ageInMs: {
      type: Number,
      default: null,
    },
    stale: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    /** A variante é escolhida conforme o tema do perfil, como no outro painel. */
    logoSrc() {
      return this.$store.getters.theme === 'light'
        ? '/static/imgs/sigma-ecm-logo-green.svg'
        : '/static/imgs/sigma-ecm-logo.svg';
    },
    /**
     * As Condições, do mais grave ao menos grave, com Fora de Operação antes de
     * Normal.
     *
     * O total de Ativos não é card: ele já está na linha de escopo, logo abaixo do
     * título, junto de instalações e sensores. Repetido como card, ocupava a
     * largura de uma Condição para dizer o que já estava dito duas linhas acima —
     * e cada card a menos é card maior para os quatro que restam.
     */
    counters() {
      return assetConditionsBySeverity.map(condition => ({
        condition,
        icon: CONDITION_ICONS[condition],
        value: this.summary[COUNTER_KEYS[condition]] || 0,
      }));
    },
    scopeLabel() {
      return this.$t('attention-map-panel.scope', {
        installations: this.summary.installationCount || 0,
        assets: this.summary.assetCount || 0,
        sensors: this.summary.sensorCount || 0,
      });
    },
    ageLabel() {
      if (this.ageInMs === null) {
        return this.$t('attention-map-panel.never-updated');
      }

      const { unit, value } = describeAge(this.ageInMs);

      return this.$t('attention-map-panel.updated-ago', {
        age: this.$t(`attention-map-panel.age.${unit}`, { count: value }),
      });
    },
  },
};
</script>

<style lang="sass" scoped>
@import "src/styles/defaults/mobile-first"
@import "src/styles/components/communication-status-panel-fluid"

// Mesmo desenho do Placar do Painel de Status de Comunicação, e os mesmos
// valores: o que muda entre os dois painéis é a escala de cor, não a
// distribuição. Ver Placar do Painel no CONTEXT.md.
//
// Os limites das faixas medem o conteúdo do próprio cabeçalho, não a janela: o
// menu lateral tira largura dele sem a janela mudar.
//
// Quatro cards na largura do painel-guia pedem 86rem de Placar; com a marca e o
// relógio ao lado, a faixa única fecha por volta de 124rem. Com o card de total a
// mais, isso só acontecia a 146rem, e um notebook caía em duas faixas — tirar o
// card devolveu a faixa única para telas comuns.
$single-band-min: 124rem
// Título numa linha só.
$single-line-title-min: 132rem

.map-summary
  background: var(--panel-surface)
  border-bottom: 1px solid var(--panel-border)
  container-name: map-summary
  container-type: inline-size
  padding: 0.7rem 1.2rem

  // Padrão: duas faixas — marca e Idade do Dado em cima, Placar embaixo na
  // largura toda. É também o que vê o navegador que não sabe medir o cabeçalho
  // (TV com navegador embarcado antigo): mais alto que o ideal, mas nunca
  // desalinhado.
  &__layout
    align-items: center
    column-gap: 1.2rem
    display: grid
    grid-template-areas: "brand freshness" "counters counters"
    grid-template-columns: minmax(0, 1fr) auto
    row-gap: 0.7rem

  &__brand
    align-items: center
    display: flex
    gap: 0.85rem
    grid-area: brand
    min-width: 0

  &__logo
    flex: none
    height: 2.8rem
    width: auto

  &__identity
    border-left: 1px solid var(--panel-border)
    min-width: 0
    padding-left: 0.85rem

  // Lido a três metros, na TV: o título se sustenta sozinho. Tamanho fixo de
  // propósito — no painel-guia o título não cresce com a tela, só o conteúdo
  // dos cards cresce. Título fluido foi o que deixou este cabeçalho
  // desproporcional ao lado do outro.
  &__title
    color: var(--panel-text)
    font-size: 2rem
    font-weight: 700
    letter-spacing: 0.04em
    line-height: 1.1
    margin: 0
    text-transform: uppercase
    text-wrap: balance

  &__scope
    color: var(--panel-text-muted)
    font-size: 0.95rem
    margin: 0

  // Colunas iguais: a largura do card não conta história nenhuma, só o número
  // conta. Card do tamanho do próprio conteúdo faria "Fora de operação" ficar
  // duas vezes maior que "Normais", sugerindo peso onde só há rótulo mais
  // comprido.
  // O teto de 108rem mantém o card na largura do painel-guia, que cabe três num
  // teto de 64rem. Vale nas duas faixas: numa tela larga em duas faixas, sem o
  // teto, cinco cards se esticariam pela largura inteira e virariam tiras.
  &__counters
    display: grid
    gap: 0.7rem
    grid-area: counters
    grid-auto-columns: minmax(0, 1fr)
    grid-auto-flow: column
    justify-self: center
    list-style: none
    margin: 0
    max-width: 86rem
    padding: 0
    width: 100%

  // Ícone numa coluna própria e conteúdo encostado à esquerda: com os cards de
  // mesma largura, ícones, números e rótulos caem na mesma vertical nos cinco.
  // Centralizar o conteúdo desalinharia tudo, porque "7" e "52" ocupam larguras
  // diferentes.
  &__counter
    align-content: center
    border: 1px solid var(--panel-border)
    border-radius: 6px
    column-gap: 0.75rem
    // Cada card é a régua do próprio texto: ícone, número e rótulo em `cqw`.
    container-type: inline-size
    display: grid
    grid-template-areas: "icon value" "icon label"
    grid-template-columns: auto 1fr
    justify-items: start
    padding: 0.5rem 1rem
    row-gap: 0.2rem

  &__counter-icon
    align-self: center
    +panel-fluid(font-size, 1.6rem, 16cqw, 2.8rem)
    grid-area: icon
    line-height: 1
    opacity: 0.9

  &__counter-value
    align-self: end
    +panel-fluid(font-size, 2rem, 22cqw, 3.6rem)
    font-variant-numeric: tabular-nums
    font-weight: 700
    grid-area: value
    line-height: 1

  // O rótulo é lido a três metros junto com o número: em corpo miúdo ele vira
  // textura e só o valor sobra, sem dizer de que condição é.
  &__counter-label
    align-self: start
    color: var(--panel-text-muted)
    +panel-fluid(font-size, 1rem, 8cqw, 1.6rem)
    font-weight: 600
    grid-area: label
    letter-spacing: 0.03em
    line-height: 1.1

  // A tinta é o destaque. Zerado, o card perde a cor e vira fundo: de longe só
  // o que existe puxa o olho. O Total nunca ganha cor: ele é a régua, e não uma
  // Condição.
  &__counter--critical
    background: var(--condition-card-critical)
    border-color: var(--condition-edge-critical)
    color: var(--condition-critical)
  &__counter--attention
    background: var(--condition-card-attention)
    border-color: var(--condition-edge-attention)
    color: var(--condition-attention)
  &__counter--normal
    background: var(--condition-card-normal)
    border-color: var(--condition-edge-normal)
    color: var(--condition-normal)

  &__counter--out-of-service
    background: var(--condition-card-out-of-service)
    border-color: var(--condition-edge-out-of-service)
    color: var(--condition-out-of-service)

  &__counter--empty
    background: var(--panel-surface-raised)
    border-color: var(--panel-border)
    color: var(--panel-text-muted)

  &__freshness
    align-items: flex-end
    display: flex
    flex-direction: column
    gap: 0.4rem
    grid-area: freshness
    justify-self: end
    text-align: right

  // Mesmo desenho da Idade do Dado no Painel de Status de Comunicação, e pelo
  // mesmo motivo: ela é o sinal de que a tela está viva, e precisa ser lida de
  // longe tanto quanto os contadores. Em 0,95rem ela era miúda ao lado do
  // cabeçalho do outro painel, e os dois ficam abertos na mesma TV.
  &__age
    color: var(--panel-text-muted)
    font-size: 1.3rem
    font-variant-numeric: tabular-nums
    font-weight: 600
    margin: 0
    // O relógio nunca quebra em duas linhas: é leitura rápida.
    white-space: nowrap

  &--stale
    background: var(--panel-stale-wash)
    border-bottom-color: var(--panel-stale)

    .map-summary__age
      color: var(--panel-stale)
      font-weight: 700

  // Faixa única quando a marca, os cinco cards e o relógio cabem lado a lado.
  // O seletor precisa da forma `& &__x` — que compila para
  // `.map-summary .map-summary__x`: o plugin de estilo escopado do Vue não
  // carimba seletores dentro de @container, e sem a raiz na frente a regra
  // perderia por especificidade para a regra base, que é carimbada.
  @container map-summary (min-width: #{$single-band-min})
    & &__layout
      grid-template-areas: "brand counters freshness"
      grid-template-columns: auto minmax(44rem, 1fr) auto

    & &__identity
      max-width: 24rem


  @container map-summary (min-width: #{$single-line-title-min})
    & &__identity
      max-width: none

  // Celular: o Placar divide a largura entre os cinco, e tudo encolhe junto
  // para caber sem rolagem lateral. As proporções entre título, número e rótulo
  // se mantêm.
  +media-max-sm()
    padding: 0.6rem 0.8rem

    &__layout
      column-gap: 0.6rem
      row-gap: 0.6rem

    &__logo
      height: 2rem

    &__identity
      padding-left: 0.6rem

    &__title
      font-size: 1.3rem

    &__scope
      font-size: 0.78rem

    &__age
      font-size: 1rem

    &__counters
      gap: 0.4rem

    &__counter
      column-gap: 0.4rem
      padding: 0.4rem 0.5rem

    &__counter-icon
      font-size: 1.1rem

    &__counter-value
      font-size: 1.4rem

    // "Fora de operação" não cabe numa linha num quinto de celular: quebra em
    // vez de estourar o card.
    &__counter-label
      font-size: 0.72rem
      line-height: 1.15
      white-space: normal
</style>
